import { Injectable, Optional } from '@angular/core';
import {
    DataChangeActionType,
    FormPendingChange,
    PendingChangeStatusType,
} from '@wdx/shared/utils';
import { IFormPendingChange, IFormPendingChangeSubForm } from '../../constants';
import {
    IExtendedFieldDefinition,
    ReactiveFormElement,
    ReactiveFormLayoutAndDefinition,
} from '../../models';

import {
    PENDING_CHANGE_REG,
    PENDING_CHANGE_SPLIT,
    UPDATE_PENDING_CHANGE_COUNTER,
} from './form-pending-changes.helper';
import { PENDING_CHANGE_LOCK_ALL } from './form-pending-changes.interface';
import { FormStaticService } from '../form-static/form-static.class';

@Injectable()
export class FormPendingChangesService {
    constructor(
        @Optional() private formStaticService?: FormStaticService,
    ) {}
    subFormPendingChanges: {
        [key: string]: {
            [key: number]: {
                [key: string]: FormPendingChange & { subItems: any };
            };
        };
    } = {};
    pendingChangesSubFormCounter: IFormPendingChangeSubForm = {};
    coc: IFormPendingChange[] = [];

    setUpPendingChanges(
        layoutAndDefinition: ReactiveFormElement[],
        formBuilderFirstLevel: any,
        pendingChanges: IFormPendingChange[] | undefined | null,
    ): ReactiveFormElement[] | undefined {
        this.coc = Array.isArray(pendingChanges)
            ? pendingChanges.map((pc) => ({ ...pc }))
            : [];
        this.sanitizeDisplayValues();
        this.updateSubFormPendingChanges();

        if (pendingChanges && layoutAndDefinition.length) {
            layoutAndDefinition?.forEach((layoutAndDefinition) => {
                layoutAndDefinition?.layoutAndDefinition?.forEach(
                    (layoutAndDefinition) => {
                        const HAS_SUB_FORM_PENDING_CHANGE =
                            !!this.subFormPendingChanges[
                                layoutAndDefinition?.name as string
                            ];

                        if (HAS_SUB_FORM_PENDING_CHANGE) {
                            this.updateSubFormPendingChangeRows(
                                layoutAndDefinition,
                                formBuilderFirstLevel,
                            );
                        }

                        if (!HAS_SUB_FORM_PENDING_CHANGE) {
                            this.updatePendingChange(layoutAndDefinition);
                        }
                    },
                );
            });
            this.resolveUnmatchedDotNotationChanges();
            return layoutAndDefinition;
        }

        this.coc = this.coc.filter((change) => change.name);
        return;
    }

    updateSubFormPendingChanges(): void {
        const removeItems: any[] = [];
        const PENDING_CHANGES = this.coc;

        PENDING_CHANGES?.forEach((pendingChange, index) => {
            // Check if sub form field is been updated
            if (pendingChange.field?.match(PENDING_CHANGE_REG)) {
                removeItems.push(index);
                const PENDING = PENDING_CHANGE_SPLIT(pendingChange.field);

                this.addDataToSubFormPendingChanges(
                    PENDING[0],
                    parseInt(PENDING[1], 10),
                    {
                        ...pendingChange,
                        field: PENDING[2],
                        name: PENDING[2],
                        ...(PENDING?.[3] ? { subItems: PENDING?.[3] } : {}),
                    },
                );
            }
        });

        removeItems.reverse().forEach((index) => {
            this.coc?.splice(index, 1);
        });
    }

    addDataToSubFormPendingChanges(
        arrayName: string,
        index: number,
        field?: any,
    ): void {
        const CHANGE_ENTIRE_ROW = [
            DataChangeActionType.Add,
            DataChangeActionType.Remove,
        ].includes(field.action);

        // For these the entire row is been changed
        if (CHANGE_ENTIRE_ROW) {
            this.subFormPendingChanges = {
                ...this.subFormPendingChanges,
                [arrayName]: {
                    ...this.subFormPendingChanges[arrayName],
                    [index]: {
                        [field.name]: field,
                    },
                },
            };
            this.updatePendingChangeCounter(arrayName, field.action, index);
        }

        // These are fields that are been updated in the row
        if (field.name) {
            this.subFormPendingChanges = {
                ...this.subFormPendingChanges,
                [arrayName]: {
                    ...this.subFormPendingChanges[arrayName],
                    [index]: {
                        ...(this.subFormPendingChanges?.[arrayName]?.[index] ||
                            {}),
                        [field.name]: {
                            ...field,
                            ...{
                                subItems: [
                                    ...(Array.isArray(field.subItems)
                                        ? field.subItems
                                        : [field.subItems].filter(Boolean)),
                                    ...(this.subFormPendingChanges[arrayName]?.[
                                        index
                                    ]?.[field.name]?.subItems
                                        ? this.subFormPendingChanges[arrayName][
                                              index
                                          ][field.name].subItems
                                        : []),
                                    ...(field.subItems?.length
                                        ? field.subItems
                                        : []),
                                ],
                            },
                        },
                    },
                },
            };

            this.updatePendingChangeCounter(
                arrayName,
                DataChangeActionType.Update,
                index,
            );
        }
    }

    updateSubFormPendingChangeRows(
        layoutAndDefinition: ReactiveFormLayoutAndDefinition,
        formBuilderFirstLevel: any,
    ): void {
        const currentSubform = layoutAndDefinition?.name as string;
        const SUB_FORM_ROW = this.subFormPendingChanges[currentSubform];

        Object.keys(SUB_FORM_ROW).forEach((key: any) => {
            const SUB_FORM_ROW_FIELD_DEFINITIONS = layoutAndDefinition
                ?.children?.[0]?.layoutAndDefinition?.[
                key as any
            ] as unknown as IExtendedFieldDefinition[];

            const pendingChangesForCurrentSubfrom: any[] = [
                ...Object.values(
                    this.subFormPendingChanges[currentSubform][key],
                ),
            ];

            pendingChangesForCurrentSubfrom.forEach((INDEX_OBJ) => {
                const UPDATE_ALL = PENDING_CHANGE_LOCK_ALL.includes(
                    INDEX_OBJ.action,
                );

                this.updatePendingChangeCounter(
                    currentSubform,
                    null as any,
                    key as number,
                    layoutAndDefinition.label,
                );

                SUB_FORM_ROW_FIELD_DEFINITIONS.forEach((field: any) => {
                    formBuilderFirstLevel[currentSubform].pendingChanges[
                        key
                    ].action = INDEX_OBJ.action;

                    if (UPDATE_ALL) {
                        field.pendingChange = {
                            name: field.label,
                            // this status has been set to Submitted to lock the form fields
                            status: PendingChangeStatusType.Submitted,
                        };
                    }

                    if (!UPDATE_ALL && field.name === INDEX_OBJ?.name) {
                        field.pendingChange = {
                            ...INDEX_OBJ,
                            name: field.label,
                            index: key,
                            subItems: INDEX_OBJ?.subItems,
                        };
                    }
                });
            });
        });
    }

    updatePendingChange(
        layoutAndDefinition: ReactiveFormLayoutAndDefinition,
    ): void {
        this.coc?.forEach((pendingChange) => {
            if (pendingChange.field === layoutAndDefinition?.name) {
                pendingChange.name = layoutAndDefinition.label as string;
                layoutAndDefinition.pendingChange = pendingChange;
            } else if (
                pendingChange.field?.includes('.') &&
                !pendingChange.field?.match(PENDING_CHANGE_REG)
            ) {
                const [parentName, childName] =
                    pendingChange.field.split('.');
                if (parentName === layoutAndDefinition?.name) {
                    const childLabel =
                        this.findChildLabel(
                            layoutAndDefinition,
                            childName,
                        ) || this.camelCaseToLabel(childName);
                    const parentLabel =
                        layoutAndDefinition.label ||
                        this.camelCaseToLabel(parentName);
                    pendingChange.name = `${parentLabel} - ${childLabel}`;
                    layoutAndDefinition.pendingChange = pendingChange;
                }
            }
        });
    }

    private findChildLabel(
        layoutAndDefinition: ReactiveFormLayoutAndDefinition,
        childName: string,
    ): string | undefined {
        const fieldPath = `${layoutAndDefinition.name}.${childName}`;
        const layoutFromFormDef =
            this.formStaticService?.getLayoutForFieldFromFormDef(fieldPath);
        if (layoutFromFormDef?.label) {
            return layoutFromFormDef.label;
        }

        for (const section of layoutAndDefinition.sectionLayoutDefinitions ??
            []) {
            const element = section.elementLayoutDefinitions?.find(
                (el) => el.name === childName,
            );
            if (element?.label) {
                return element.label;
            }
        }
        return undefined;
    }

    private camelCaseToLabel(value: string): string {
        return value
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
    }

    private resolveUnmatchedDotNotationChanges(): void {
        this.coc?.forEach((pendingChange) => {
            if (
                !pendingChange.name &&
                pendingChange.field?.includes('.') &&
                !pendingChange.field?.match(PENDING_CHANGE_REG)
            ) {
                const [parentName, childName] =
                    pendingChange.field.split('.');
                const layoutFromFormDef =
                    this.formStaticService?.getLayoutForFieldFromFormDef(
                        pendingChange.field,
                    );
                const childLabel = layoutFromFormDef?.label ||
                    this.camelCaseToLabel(childName);
                const parentLabel = this.camelCaseToLabel(parentName);
                pendingChange.name = `${parentLabel} - ${childLabel}`;
            }
        });
    }

    private sanitizeDisplayValues(): void {
        this.coc?.forEach((pendingChange) => {
            if (pendingChange.originalDisplayValue === '{NULL}') {
                pendingChange.originalDisplayValue = '-';
            }
            if (pendingChange.targetDisplayValue === '{NULL}') {
                pendingChange.targetDisplayValue = '-';
            }
        });
    }

    updatePendingChangeCounter(
        name: string,
        type: DataChangeActionType,
        index: number,
        label?: string | null,
    ): void {
        this.pendingChangesSubFormCounter = UPDATE_PENDING_CHANGE_COUNTER(
            this.pendingChangesSubFormCounter,
            name,
            type,
            label,
            index,
        );
    }
}
